import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faDownload,
  faExpand,
  faFilter,
  faExclamationTriangle,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { format, differenceInHours, isValid, parseISO } from "date-fns";
import { calculateBusinessHours, KELLANOVA_THRESHOLDS, getPerformanceCategory } from "../utils/businessHours";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProcessBottleneckAnalyzer = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // State for chart data
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  // State for process phases and overall insights
  const [insights, setInsights] = useState({
    totalProcessedReferences: 0,
    averageTotalProcessingTime: 0,
    topBottlenecks: [],
    entryPointBottlenecks: {},
    processPhaseTimes: {},
    recommendations: [],
  });

  // State for filter
  const [selectedEntryPoint, setSelectedEntryPoint] = useState("all");
  const [selectedMetric, setSelectedMetric] = useState("average"); // average, median, or max

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // Process data when it changes
  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      processBottleneckData();
    }
  }, [data, isLoading, selectedEntryPoint, selectedMetric]);

  // Calculate processing times for different phases
  const processBottleneckData = () => {
    // Filter data by entry point if selected
    const filteredData =
      selectedEntryPoint === "all"
        ? data
        : data.filter(
            (item) =>
              item.header?.pedimento?.pedimento_entry_point ===
              selectedEntryPoint
          );

    if (filteredData.length === 0) {
      setChartData({ labels: [], datasets: [] });
      return;
    }

    // Define process phases and their date fields
    const processPhases = [
      {
        id: "initial_processing",
        label: "Initial Processing",
        startField: "date_init",
        endField: "date_inbound",
        color: "rgba(54, 162, 235, 0.7)", // Blue
      },
      {
        id: "document_preparation",
        label: "Document Preparation",
        startField: "date_inbound",
        endField: "date_doda",
        color: "rgba(75, 192, 192, 0.7)", // Teal
      },
      {
        id: "validation_approval",
        label: "Validation & Approval",
        startField: "date_doda",
        endField: "date_end",
        color: "rgba(153, 102, 255, 0.7)", // Purple
      },
      {
        id: "crossing_completion",
        label: "Crossing & Completion",
        startField: "date_end",
        endField: "date_crossing",
        color: "rgba(255, 159, 64, 0.7)", // Orange
      },
    ];

    // Calculate processing times for each phase for each entry
    const processingTimes = {};
    let totalValidEntries = 0;
    let totalProcessingTime = 0;

    // Initialize processing times structure
    processPhases.forEach((phase) => {
      processingTimes[phase.id] = {
        times: [],
        totalTime: 0,
        count: 0,
        avgTime: 0,
        medianTime: 0,
        maxTime: 0,
        entryPointBreakdown: {},
      };
    });

    // Process each data entry
    filteredData.forEach((item) => {
      const datesData = item.header?.detail?.dates_data;
      const entryPoint =
        item.header?.pedimento?.pedimento_entry_point || "unknown";

      if (!datesData) return;

      let hasValidDates = false;
      let entryTotalTime = 0;

      // Calculate time for each process phase
      processPhases.forEach((phase) => {
        const startDate = datesData[phase.startField]
          ? parseISO(datesData[phase.startField])
          : null;
        const endDate = datesData[phase.endField]
          ? parseISO(datesData[phase.endField])
          : null;

        if (
          startDate &&
          endDate &&
          isValid(startDate) &&
          isValid(endDate) &&
          endDate > startDate
        ) {
          // Calculate business hours excluding weekends
          const phaseHours = calculateBusinessHours(startDate, endDate);

          // Skip unreasonable values (over 500 business hours for a single phase)
          if (phaseHours === null || phaseHours > 500) return;

          processingTimes[phase.id].times.push(phaseHours);
          processingTimes[phase.id].totalTime += phaseHours;
          processingTimes[phase.id].count += 1;

          // Track by entry point
          if (!processingTimes[phase.id].entryPointBreakdown[entryPoint]) {
            processingTimes[phase.id].entryPointBreakdown[entryPoint] = {
              times: [],
              totalTime: 0,
              count: 0,
            };
          }

          processingTimes[phase.id].entryPointBreakdown[entryPoint].times.push(
            phaseHours
          );
          processingTimes[phase.id].entryPointBreakdown[entryPoint].totalTime +=
            phaseHours;
          processingTimes[phase.id].entryPointBreakdown[entryPoint].count += 1;

          entryTotalTime += phaseHours;
          hasValidDates = true;
        }
      });

      // Count valid entries for total average calculation
      if (hasValidDates) {
        totalValidEntries += 1;
        totalProcessingTime += entryTotalTime;
      }
    });

    // Calculate statistics for each phase
    processPhases.forEach((phase) => {
      const phaseTimes = processingTimes[phase.id].times;

      if (phaseTimes.length > 0) {
        // Average
        processingTimes[phase.id].avgTime =
          processingTimes[phase.id].totalTime / processingTimes[phase.id].count;

        // Median
        const sortedTimes = [...phaseTimes].sort((a, b) => a - b);
        const midIndex = Math.floor(sortedTimes.length / 2);
        processingTimes[phase.id].medianTime =
          sortedTimes.length % 2 === 0
            ? (sortedTimes[midIndex - 1] + sortedTimes[midIndex]) / 2
            : sortedTimes[midIndex];

        // Max (95th percentile to avoid outliers)
        const p95Index = Math.floor(sortedTimes.length * 0.95);
        processingTimes[phase.id].maxTime =
          sortedTimes[Math.min(p95Index, sortedTimes.length - 1)];

        // Calculate averages for each entry point
        Object.keys(processingTimes[phase.id].entryPointBreakdown).forEach(
          (ep) => {
            const epData = processingTimes[phase.id].entryPointBreakdown[ep];
            epData.avgTime = epData.totalTime / epData.count;
          }
        );
      }
    });

    // Identify bottlenecks based on the selected metric
    const getPhaseValue = (phase) => {
      switch (selectedMetric) {
        case "median":
          return processingTimes[phase.id].medianTime;
        case "max":
          return processingTimes[phase.id].maxTime;
        default:
          return processingTimes[phase.id].avgTime;
      }
    };

    // Prepare chart data
    const labels = processPhases.map((phase) => phase.label);
    const metricValues = processPhases.map((phase) => getPhaseValue(phase));

    // Sort phases by time for bottleneck identification
    const sortedPhases = [...processPhases]
      .map((phase) => ({
        ...phase,
        value: getPhaseValue(phase),
      }))
      .sort((a, b) => b.value - a.value);

    // Identify top bottlenecks
    const topBottlenecks = sortedPhases
      .filter((phase) => phase.value > 0)
      .slice(0, 2)
      .map((phase) => ({
        phaseId: phase.id,
        phaseLabel: phase.label,
        value: phase.value,
      }));

    // Identify entry point specific bottlenecks
    const entryPointBottlenecks = {};
    const allEntryPoints = new Set();

    // Collect all entry points first
    processPhases.forEach((phase) => {
      Object.keys(processingTimes[phase.id].entryPointBreakdown).forEach(
        (ep) => {
          allEntryPoints.add(ep);
        }
      );
    });

    // Calculate bottlenecks for each entry point
    allEntryPoints.forEach((ep) => {
      const phaseTimes = processPhases
        .map((phase) => {
          const epData = processingTimes[phase.id].entryPointBreakdown[ep];
          return {
            phase: phase,
            time: epData ? epData.avgTime : 0,
          };
        })
        .filter((pt) => pt.time > 0);

      // Sort by time (descending)
      phaseTimes.sort((a, b) => b.time - a.time);

      if (phaseTimes.length > 0) {
        entryPointBottlenecks[ep] = {
          worstPhase: phaseTimes[0].phase.id,
          worstPhaseLabel: phaseTimes[0].phase.label,
          worstPhaseTime: phaseTimes[0].time,
        };
      }
    });

    // Generate recommendations based on identified bottlenecks
    const recommendations = [];

    if (topBottlenecks.length > 0) {
      const worstPhase = topBottlenecks[0];
      recommendations.push(
        `Focus improvement efforts on the ${
          worstPhase.phaseLabel
        } phase (${worstPhase.value.toFixed(1)} hours)`
      );

      // Add specific recommendations based on the bottleneck phase
      switch (worstPhase.phaseId) {
        case "initial_processing":
          recommendations.push(
            "Streamline initial paperwork and data collection processes"
          );
          break;
        case "document_preparation":
          recommendations.push(
            "Optimize document preparation workflows and templates"
          );
          break;
        case "validation_approval":
          recommendations.push(
            "Review approval chains and validation procedures"
          );
          break;
        case "crossing_completion":
          recommendations.push(
            "Analyze crossing logistics and communication channels"
          );
          break;
      }
    }

    // Add entry point specific recommendations
    const worstEntryPoint = Object.entries(entryPointBottlenecks).sort(
      (a, b) => b[1].worstPhaseTime - a[1].worstPhaseTime
    )[0];

    if (worstEntryPoint) {
      const [ep, epData] = worstEntryPoint;
      recommendations.push(
        `Address ${epData.worstPhaseLabel} delays at Entry Point ${ep}`
      );
    }

    // Set chart data
    setChartData({
      labels,
      datasets: [
        {
          label: `${
            selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)
          } Processing Time`,
          data: metricValues,
          backgroundColor: processPhases.map((phase) => phase.color),
          borderColor: processPhases.map((phase) =>
            phase.color.replace("0.7", "1")
          ),
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    });

    // Update insights
    setInsights({
      totalProcessedReferences: totalValidEntries,
      averageTotalProcessingTime:
        totalValidEntries > 0 ? totalProcessingTime / totalValidEntries : 0,
      topBottlenecks,
      entryPointBottlenecks,
      processPhaseTimes: processingTimes,
      recommendations,
    });
  };

  // Get all available entry points
  const getEntryPointOptions = () => {
    if (!data || data.length === 0) return [];

    const entryPoints = new Set();

    data.forEach((item) => {
      const entryPoint = item.header?.pedimento?.pedimento_entry_point;
      if (entryPoint) {
        entryPoints.add(entryPoint);
      }
    });

    return Array.from(entryPoints).sort();
  };

  // Chart options
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: "Hours",
          font: {
            weight: "bold",
          },
        },
        beginAtZero: true,
      },
      y: {
        title: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw.toFixed(1)} hours`;
          },
        },
      },
    },
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-red-600 mr-2"
        />
        <span className="text-gray-600">Analyzing bottlenecks...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || chartData.labels.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faWrench}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No process data available</span>
      </div>
    );
  }

  // Entry point options
  const entryPointOptions = getEntryPointOptions();

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faWrench} className="text-red-600 mr-2" />
          Process Bottleneck Analyzer
        </h2>

        {/* Filter Controls */}
        <div className="flex items-center space-x-3">
          <div>
            <select
              value={selectedEntryPoint}
              onChange={(e) => setSelectedEntryPoint(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-red-500"
            >
              <option value="all">All Entry Points</option>
              {entryPointOptions.map((ep) => (
                <option key={ep} value={ep}>
                  Entry Point {ep}
                </option>
              ))}
            </select>
          </div>

          <div className="flex rounded-md overflow-hidden border border-gray-300">
            <button
              className={`px-2 py-1 text-xs transition-colors ${
                selectedMetric === "average"
                  ? "bg-red-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedMetric("average")}
            >
              Average
            </button>
            <button
              className={`px-2 py-1 text-xs transition-colors ${
                selectedMetric === "median"
                  ? "bg-red-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedMetric("median")}
            >
              Median
            </button>
            <button
              className={`px-2 py-1 text-xs transition-colors ${
                selectedMetric === "max"
                  ? "bg-red-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedMetric("max")}
            >
              95th %ile
            </button>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">
            Average Total Processing Time
          </div>
          <div className="text-md font-bold">
            {insights.averageTotalProcessingTime.toFixed(1)} hours
          </div>
        </div>

        <div className="bg-red-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Primary Bottleneck</div>
          <div className="text-md font-bold text-red-700">
            {insights.topBottlenecks.length > 0
              ? insights.topBottlenecks[0].phaseLabel
              : "N/A"}
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">References Analyzed</div>
          <div className="text-md font-bold">
            {insights.totalProcessedReferences}
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Bar data={chartData} options={options} />
      </div>

      {/* Bottleneck Insights */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-red-500 mr-1"
              />
              Process Improvement Opportunities
            </h4>
            <ul className="text-xs text-gray-600 space-y-1">
              {insights.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-4 text-red-500 mr-1">•</span>
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex space-x-1">
            <button
              title="Download analysis"
              className="p-1 hover:text-red-600 transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
            <button
              title="Expand analysis"
              className="p-1 hover:text-red-600 transition-colors"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <FontAwesomeIcon icon={faExpand} />
            </button>
            <button
              title="Filter data"
              className="p-1 hover:text-red-600 transition-colors"
            >
              <FontAwesomeIcon icon={faFilter} />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-5/6 h-5/6 max-w-7xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Detailed Process Bottleneck Analysis
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div
              className="p-4 overflow-y-auto"
              style={{ height: "calc(100% - 64px)" }}
            >
              {/* Process Flow Visualization */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Process Flow Breakdown
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    {/* Process Flow Steps */}
                    {chartData.labels.map((label, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 ${
                            insights.topBottlenecks.length > 0 &&
                            insights.topBottlenecks[0].phaseLabel === label
                              ? "bg-red-100 border-2 border-red-500"
                              : "bg-gray-100"
                          }`}
                        >
                          <span className="text-lg font-bold">{index + 1}</span>
                        </div>
                        <div className="text-xs text-center font-medium">
                          {label}
                        </div>
                        <div className="text-xs mt-1 font-bold">
                          {chartData.datasets[0].data[index].toFixed(1)}h
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Connecting arrows */}
                  <div className="flex items-center justify-center mt-2">
                    {Array(chartData.labels.length - 1)
                      .fill(0)
                      .map((_, i) => (
                        <div key={i} className="flex-1 px-2">
                          <div className="border-t-2 border-gray-300 border-dashed"></div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Large Chart */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-6">
                <div style={{ height: "300px" }}>
                  <Bar data={chartData} options={options} />
                </div>
              </div>

              {/* Entry Point Comparison Table */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Entry Point Process Comparison
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Entry Point
                        </th>
                        {chartData.labels.map((label, index) => (
                          <th
                            key={index}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {label}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Primary Bottleneck
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.keys(insights.entryPointBottlenecks).map(
                        (ep, idx) => {
                          const bottleneck = insights.entryPointBottlenecks[ep];

                          return (
                            <tr
                              key={ep}
                              className={
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                EP {ep}
                              </td>

                              {chartData.labels.map((label, labelIdx) => {
                                // Find the corresponding process phase
                                const phaseId = Object.keys(
                                  insights.processPhaseTimes
                                )[labelIdx];
                                const phaseData =
                                  insights.processPhaseTimes[phaseId];

                                // Get entry point specific data
                                const epData =
                                  phaseData.entryPointBreakdown[ep];
                                const timeValue = epData ? epData.avgTime : 0;

                                // Determine if this is the bottleneck for this entry point
                                const isBottleneck =
                                  bottleneck &&
                                  bottleneck.worstPhaseLabel === label;

                                return (
                                  <td
                                    key={labelIdx}
                                    className={`px-3 py-2 whitespace-nowrap text-sm ${
                                      isBottleneck
                                        ? "bg-red-50 font-bold text-red-700"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {timeValue.toFixed(1)}h
                                  </td>
                                );
                              })}

                              <td className="px-3 py-2 whitespace-nowrap text-sm text-red-600 font-medium">
                                {bottleneck.worstPhaseLabel}
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
                <h4 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                  <FontAwesomeIcon
                    icon={faWrench}
                    className="text-red-600 mr-2"
                  />
                  Detailed Recommendations
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Process Improvements
                    </h5>
                    <ul className="space-y-2">
                      {insights.recommendations.map((recommendation, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-4 text-red-500 mr-1">
                            •
                          </span>
                          <span className="text-sm">{recommendation}</span>
                        </li>
                      ))}
                      {insights.topBottlenecks.length > 0 &&
                        insights.topBottlenecks[0].phaseId ===
                          "document_preparation" && (
                          <>
                            <li className="flex items-start">
                              <span className="inline-block w-4 text-red-500 mr-1">
                                •
                              </span>
                              <span className="text-sm">
                                Consider implementing document templates and
                                standardized workflows
                              </span>
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-4 text-red-500 mr-1">
                                •
                              </span>
                              <span className="text-sm">
                                Evaluate opportunities for automation in
                                document processing
                              </span>
                            </li>
                          </>
                        )}
                      {insights.topBottlenecks.length > 0 &&
                        insights.topBottlenecks[0].phaseId ===
                          "validation_approval" && (
                          <>
                            <li className="flex items-start">
                              <span className="inline-block w-4 text-red-500 mr-1">
                                •
                              </span>
                              <span className="text-sm">
                                Streamline approval workflows with parallel
                                processing where possible
                              </span>
                            </li>
                            <li className="flex items-start">
                              <span className="inline-block w-4 text-red-500 mr-1">
                                •
                              </span>
                              <span className="text-sm">
                                Review validation criteria for opportunities to
                                simplify
                              </span>
                            </li>
                          </>
                        )}
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      Next Steps
                    </h5>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-red-500 mr-1">
                          1.
                        </span>
                        <span className="text-sm">
                          Conduct detailed process mapping for{" "}
                          {insights.topBottlenecks.length > 0
                            ? insights.topBottlenecks[0].phaseLabel
                            : "identified bottlenecks"}
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-red-500 mr-1">
                          2.
                        </span>
                        <span className="text-sm">
                          Set specific time reduction targets for each process
                          phase
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-red-500 mr-1">
                          3.
                        </span>
                        <span className="text-sm">
                          Compare workflows between high and low performing
                          entry points
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-red-500 mr-1">
                          4.
                        </span>
                        <span className="text-sm">
                          Implement workflow improvements and monitor metrics
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessBottleneckAnalyzer;
